import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import TestimonyCard from "@/components/TestimonyCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, MessageSquareHeart } from "lucide-react";
import { toast } from "sonner";

export default function Testimonies() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState("community");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/testimonies").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const share = async () => {
    if (!body.trim()) return toast.error("Write something before sharing.");
    setSending(true);
    try {
      await api.post("/testimonies", { title, body, visibility });
      setTitle(""); setBody(""); setOpen(false);
      toast.success("Thank you for sharing a part of yourself.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const VIS = [["community", "Visible to the community"], ["anonymous", "Anonymous to the community"], ["listeners", "Only to those who listen"]];

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="eyebrow mb-4">Testimonies</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">Voices that recognise one another</h1>
        </div>
        <Button className="btn-gold rounded-full shrink-0 text-sm font-medium" onClick={() => setOpen(!open)} data-testid="new-testimony-btn">
          <Plus className="w-4 h-4 mr-1" /> Share
        </Button>
      </div>
      <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed mb-12">
        No likes, no followers. Only people who look at and recognise one another for what they live. You choose what to share, and with whom.
      </p>

      {open && (
        <div className="bg-secondary/50 border border-border rounded-3xl p-8 mb-12 animate-fade-in-up">
          <Input placeholder="A title (optional)" className="rounded-xl bg-background mb-4" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="testimony-title-input" />
          <Textarea placeholder="Share a part of yourself…" className="rounded-xl bg-background min-h-40 mb-4" value={body} onChange={(e) => setBody(e.target.value)} data-testid="testimony-body-input" />
          <RadioGroup value={visibility} onValueChange={setVisibility} className="flex flex-wrap gap-3 mb-6">
            {VIS.map(([v, l], i) => (
              <label key={v} className="flex items-center gap-2 text-sm border border-border rounded-full px-4 py-2 cursor-pointer bg-background" data-testid={`visibility-option-${v}`}>
                <RadioGroupItem value={v} data-testid={`visibility-radio-${v}`} /> {l}
              </label>
            ))}
          </RadioGroup>
          <Button className="btn-gold rounded-full text-sm font-medium" disabled={sending} onClick={share} data-testid="testimony-submit-btn">
            {sending ? "Sending…" : "Share"}
          </Button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-card border border-border rounded-3xl p-14 text-center">
          <MessageSquareHeart className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <p className="font-heading text-2xl mb-2">No voices yet</p>
          <p className="text-muted-foreground">You could be the first to share your story.</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="testimonies-list">
          {items.map((t) => <TestimonyCard key={t.id} t={t} onChange={load} />)}
        </div>
      )}
    </div>
  );
}
